import '../css/FICAutocomplete.css'

import React, {
  ChangeEvent,
  forwardRef,
  MutableRefObject,
  useEffect,
  useState,
} from 'react'
import Autocomplete from 'react-autocomplete'

import { SearchResult } from '../../components/definitions'
import { AutocompleteProps } from '../../components/HelpAutocomplete/helpautocomplete.components'

const FICAutocomplete = forwardRef<Autocomplete, AutocompleteProps>(
  (props, autocompleteRef) => {
    const [firstLoad, setFirstLoad] = useState<boolean>(false)
    const [filter, setFilter] = useState<string>(props.value)
    const [scheduledChange, setScheduledChange] = useState<NodeJS.Timeout>(null)
    const [scheduledLoadOptions, setScheduledLoadOptions] =
      useState<NodeJS.Timeout>(null)
    const [lastOnChangeValue, setLastOnChangeValue] = useState<string>(
      props.value
    )
    const [useInternalValue, setUseInternalValue] = useState<boolean>(false)
    const [internalValue, setInternalValue] = useState<string>(props.value)

    useEffect(() => {
      if (props.autoFocus) {
        setTimeout(() => {
          focus()
          setTimeout(() => {
            setUseInternalValue(false)
          }, 0)
        }, 100)
      }
    }, [])

    const focus = () => {
      if (autocompleteRef) {
        const a = autocompleteRef as MutableRefObject<Autocomplete>
        a.current.focus()
      }
    }

    const shouldUseInternalValue = () => {
      return props.changeOnBlur || (props.uncontrolled && props.changeDelay)
    }

    const getDisplayValue = () => {
      return shouldUseInternalValue() && useInternalValue
        ? internalValue || ''
        : props.value != null
        ? props.value
        : ''
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      // Options
      setFilter(value)
      if (props.loadOptions) {
        cancelScheduledLoadOptions()
        if (0 <= value.length) {
          scheduleLoadOptions(value)
        }
      }
      if (shouldUseInternalValue()) {
        setInternalValue(value)
      }
      if (props.changeDelay) {
        cancelScheduledChange()
        scheduleChange(value)
      } else {
        executeOnChange(value)
      }
    }

    const executeOnChange = (value: string) => {
      if (props.onChange && value !== lastOnChangeValue) {
        setLastOnChangeValue(value)
        props.onChange(props.name, value, null, 'autocomplete')
      }
    }

    const handleSelect = (value: string, data: SearchResult) => {
      const key = valueKey as keyof SearchResult
      const label = labelKey as keyof SearchResult
      value = String(data[key ? key : label])
      cancelScheduledChange()
      cancelScheduledLoadOptions()
      if (shouldUseInternalValue()) {
        setInternalValue(value)
      }
      if (!props.preventChangeOnSelect) {
        executeOnChange(value)
      }
      if (props.onSelect) {
        setLastOnChangeValue(value)
        props.onSelect(value, data)
      }
    }

    const handleFocus = (e) => {
      if (shouldUseInternalValue()) {
        setUseInternalValue(true)
        setInternalValue(props.value)
      }
      if (props.onFocus) {
        props.onFocus(e)
      }
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const value = e.target.value
      setUseInternalValue(false)
      if (props.changeOnBlur) {
        executeOnChange(value)
      }
      if (props.onBlur) {
        props.onBlur(e)
      }
    }

    const scheduleChange = (value: string) => {
      const scheduledChange = setTimeout(() => {
        executeOnChange(value)
      }, props.changeDelay)
      setScheduledChange(scheduledChange)
    }

    const cancelScheduledChange = () => {
      if (scheduledChange) {
        clearTimeout(scheduledChange)
      }
    }

    const scheduleLoadOptions = (value: string) => {
      const scheduledLoadOptions = setTimeout(
        () => props.loadOptions(value),
        props.loadOptionsDelay
      )
      setScheduledLoadOptions(scheduledLoadOptions)
    }

    const cancelScheduledLoadOptions = () => {
      if (scheduledLoadOptions) {
        clearTimeout(scheduledLoadOptions)
      }
    }

    const {
      idKey,
      labelKey,
      loadOptions,
      maxMenuHeight,
      options,
      placeholder,
      renderItem,
      renderMenuContent,
      shouldItemRender,
      triggerLength,
      validator,
      valueKey,
    } = props
    let className = 'fic-autocomplete'
    if (maxMenuHeight) {
      className += ' fic-height-' + String(maxMenuHeight)
    }
    if (props.className) {
      className += ' ' + props.className
    }
    let inputClassName = 'fic-input-text-input'
    if (validator && !validator.valid) {
      inputClassName += ' fic-input-invalid'
    }
    if (props.disabled) {
      inputClassName += ' fic-input-disabled'
    }
    const value = getDisplayValue()

    return (
      <Autocomplete
        ref={autocompleteRef}
        wrapperProps={{ className: className }}
        getItemValue={(item: SearchResult) =>
          String(
            item[
              (idKey as keyof SearchResult) || (labelKey as keyof SearchResult)
            ]
          )
        }
        wrapperStyle={{
          position: 'relative',
        }}
        renderMenu={(items, value, style) => {
          if (renderMenuContent) {
            return renderMenuContent(items, value, style)
          }

          if (!items || items.length === 0) {
            return <></>
          }
          style.minWidth = 0
          if (maxMenuHeight) {
            style.maxHeight = 30 * maxMenuHeight
          }
          const displayMenu = triggerLength > filter.length ? 'none' : 'block'
          return (
            <div
              className='fic-autocomplete-menu-outer'
              style={{ display: displayMenu }}
            >
              <div style={style} className='fic-autocomplete-menu'>
                {items}
              </div>
            </div>
          )
        }}
        menuStyle={{
          position: 'absolute',
          top: 30,
          left: 0,
        }}
        items={options}
        renderItem={(item: SearchResult, isHighlighted, styles) => {
          let className = 'fic-autocomplete-option'
          if (isHighlighted) {
            className += ' is-focused'
          }
          return renderItem ? (
            renderItem(item, className, styles)
          ) : (
            <div
              key={String(
                item[
                  (idKey as keyof SearchResult) ||
                    (labelKey as keyof SearchResult)
                ]
              )}
              className={className}
            >
              {item[labelKey]}
            </div>
          )
        }}
        shouldItemRender={(item: SearchResult, value) => {
          return shouldItemRender
            ? shouldItemRender(item, value)
            : item[labelKey] &&
                String(item[labelKey as keyof SearchResult])
                  .toLowerCase()
                  .indexOf(value.toLowerCase()) > -1
        }}
        inputProps={{
          className: inputClassName,
          placeholder: placeholder,
          onFocus: handleFocus,
          onBlur: handleBlur,
          onKeyPress: props.onKeyPress,
          onKeyDown: props.onKeyDown,
          onKeyUp: props.onKeyUp,
          onClick: props.onClick,
          onMouseOver: props.onMouseOver,
          onMouseOut: props.onMouseOut,
          autoComplete: 'new-password',
          disabled: props.disabled,
          maxLength: props.maxLength,
        }}
        value={value}
        onChange={handleChange}
        onSelect={handleSelect}
        onMenuVisibilityChange={(isOpen) => {
          if (
            isOpen &&
            !firstLoad &&
            loadOptions &&
            triggerLength <= filter.length
          ) {
            setFirstLoad(true)
            loadOptions('')
          }
        }}
      />
    )
  }
)

FICAutocomplete.displayName = 'ficautocomplete'

export default FICAutocomplete
